// What is left of the header after the header stopped existing.
//
// The chip rail, the layer dropdown, the time-of-day select and the "?" popover
// all went; the sentence (ui/sentence.ts) and the two-step lens picker
// (ui/lens-menu.ts) replaced them. This file keeps its name and its three
// genuinely shared jobs:
//
//   activityKeys()  — the one list every activity-shaped thing goes through, so
//                     the lens menu, the city calendar and the pin sync can
//                     never disagree about which row is which. That is why it
//                     lives here and is HANDED to the city panel rather than
//                     each computing its own.
//   unlockCustom()  — the moment the custom rating becomes the user's own,
//                     reachable from four places (the lens menu, a ?r= link, a
//                     restored session, the photo seeder).
//   the Layers surface and the two credit toggles — chrome that belongs to no
//                     one view.

import { $ } from '../dom.ts';
import { state, store } from '../state.ts';
import { buildCalendar } from '../city/panel.ts';
import { windAvailable } from '../wind-state.ts';
import { VAR, VARIABLES, VARIANT_GROUPS } from '../../lib/variables.ts';
import { peaksShowing } from '../myday.ts';
import type { VarKey, VariantGroup } from '../../lib/variables.ts';

export interface ChromeDeps {
  persist: () => void;
  /** The data-source credit line — owned by the map, revealed by the footers here. */
  credits: () => string;
  setFieldVisible: (on: boolean) => void;
  setCitiesOn: (on: boolean) => void;
  setWindOn: (on: boolean) => void;
  setFieldOpacity: (v: number) => void;
}

let deps: ChromeDeps;

export function initChrome(d: ChromeDeps): void {
  deps = d;
}

// A chip stayed lit for any of its variants; the lens menu still needs to know
// which group a raw layer belongs to.
export function groupFor(varKey: VarKey): VariantGroup | null {
  for (const g of Object.values(VARIANT_GROUPS) as VariantGroup[]) {
    if (g.members.some(([k]) => k === varKey)) return g;
  }
  return null;
}

// The activities as the user currently sees them: the curated five always, the
// custom score only once unlocked.
export function activityKeys(): VarKey[] {
  return VARIABLES.filter((v) => VAR[v].category === 'activities' && (!VAR[v].custom || state.customOn));
}

// Reveal the custom score — the moment it becomes the user's own. Safe to call
// repeatedly. The lens menu redraws itself from state, so unlike the chip bar it
// replaced there is no picker to rebuild here.
export function unlockCustom() {
  if (state.customOn) return;
  state.customOn = true;
  if (state.view === 'city' && state.series) buildCalendar(state.series);
  deps.persist();
}

// ── the Layers surface ──────────────────────────────────────────────────────
// Field / Cities / Wind / opacity, plus the wind-arrow key. The redesign never
// drew this one, so it keeps the controls it has and takes the new panel
// styling — a working feature is not a placeholder.
export function closeLayerMenu(): void {
  if (state.menu === 'layers') store.set({ menu: null });
}

export function renderLayerMenu(): void {
  const menu = $('layers-menu');
  const cog = $('layers-cog');
  const open = state.menu === 'layers';
  menu.hidden = !open;
  cog.classList.toggle('open', open);
  cog.setAttribute('aria-expanded', String(open));
}

// ── the corner words ────────────────────────────────────────────────────────
// "Find your day · Compare places · Search a city · Layers". The one whose
// surface or page is open takes the sentence's own active affordance — the
// accent underline — so the four read as the same kind of editable text.
export function renderCornerWords(): void {
  const findOn = state.view === 'landing' || peaksShowing() || String(state.menu).startsWith('day-');
  const compareOn = state.view === 'compare' || (state.menu === 'search' && state.searchFor === 'compare');
  $('find-day').classList.toggle('on', findOn);
  $('compare-open').classList.toggle('on', compareOn);
  $('open-search').classList.toggle('on', state.menu === 'search' && state.searchFor === 'open');
}

// ── wiring ──────────────────────────────────────────────────────────────────
export function wireChrome() {
  // Map view: the same "+" reveals the data-source credits, collapsed by default.
  $('map-src-toggle').addEventListener('click', () => {
    const btn = $('map-src-toggle');
    const text = $('map-foot-text');
    const showing = btn.textContent === '−';
    if (showing) {
      text.hidden = true;
      btn.textContent = '+';
      btn.setAttribute('aria-label', 'Show data sources');
    } else {
      text.textContent = deps.credits();
      text.hidden = false;
      btn.textContent = '−';
      btn.setAttribute('aria-label', 'Hide data sources');
    }
  });

  // "Layers" → the Field / Cities / Wind panel. Like the sentence's words, this
  // only states which surface should be open; renderLayerMenu is the subscriber.
  $('layers-cog').addEventListener('click', (e) => {
    e.stopPropagation();
    store.set({ menu: state.menu === 'layers' ? null : 'layers' });
  });

  // The layer toggles inside that panel.
  $('toggle-field').addEventListener('click', () => deps.setFieldVisible(!state.fieldOn));
  $('toggle-cities').addEventListener('click', () => deps.setCitiesOn(!state.citiesOn));
  const windBtn = $('toggle-wind');
  if (windAvailable()) windBtn.addEventListener('click', () => deps.setWindOn(!state.windOn));
  else windBtn.hidden = true;
  const opacity = $<HTMLInputElement>('field-opacity');
  opacity.value = String(Math.round(state.fieldOpacity * 100));
  opacity.addEventListener('input', (e) => deps.setFieldOpacity(Number((e.target as HTMLInputElement).value) / 100));

  // An outside click closes it. The sentence's own closer covers the other three
  // surfaces; this one covers the panel it owns, and both go through state.menu
  // so they cannot fight.
  document.addEventListener('click', (e) => {
    if (state.menu !== 'layers') return;
    const menu = $('layers-menu');
    if (!menu.contains(e.target as Node | null)) store.set({ menu: null });
  });
}
